import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import Modal from "../../components/ui-components/Modal";
import Input from "../../components/form-components/Input";
import { Dropdown } from "../../components/form-components/Dropdown";
import ScopeNodeTreeSelect from "../../components/Budgets/ScopeNodeTreeSelect";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import {
  useUserScopeGrants,
  createUserScopeGrant,
  deleteUserScopeGrant,
  listBudgetDimensions,
  scopeGrantsKey,
} from "./scopeApi";

let _clauseCounter = 0;
const nextClauseId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  _clauseCounter += 1;
  return `draft-clause-${_clauseCounter}`;
};

// A draft clause is edited as { dimensionId, nodeId, nodeLabel } — numeric ids,
// matching ScopeNodeTreeSelect's contract. It's translated to the API's
// { dimensionCode, operator, nodeId } shape only on save.
const emptyDraftClause = () => ({
  id: nextClauseId(),
  dimensionId: null,
  nodeId: null,
  nodeLabel: null,
});

// Maps backend error codes (see backend_go cmd/api/scope_grants_handlers.go)
// to messages a tenant admin can act on.
function friendlyGrantError(err, action) {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  switch (code) {
    case "INVALID_DIMENSION":
      return "One of the selected dimensions isn't recognized. Refresh and try again.";
    case "INVALID_OPERATOR":
      return "That condition type isn't supported.";
    case "INVALID_SCOPE_GRANT":
      return serverMessage || "This scope grant is incomplete or invalid.";
    case "DUPLICATE_SCOPE_GRANT":
      return "An identical scope grant already exists for this user.";
    case "SCOPE_STILL_REQUIRED":
      return "This is the user's last data-scope grant, and their role requires at least one. Add another grant before removing this one.";
    default:
      return serverMessage || err?.message || `Failed to ${action} scope grant`;
  }
}

/**
 * UserScopeGrants — "Data scope" panel on the user detail page. Lists the
 * user's data-scope grants (label + AND'd dimension/subtree clauses) and, for
 * viewers holding roles.assign or users.manage, lets them add a new grant
 * (dimension + subtree picker, reusing ScopeNodeTreeSelect) or remove one.
 */
const UserScopeGrants = ({ userId }) => {
  // Call both capability hooks unconditionally — `||` would short-circuit the
  // second, violating the rules of hooks (hooks must run in the same order
  // every render). Combine the results afterward.
  const hasRolesAssign = useHasCapability(CAPS.ROLES_ASSIGN);
  const hasUsersManage = useHasCapability(CAPS.USERS_MANAGE);
  const canManage = hasRolesAssign || hasUsersManage;

  const queryClient = useQueryClient();
  const grantsQ = useUserScopeGrants(userId);
  const dimsQ = useQuery({
    queryKey: ["budget-dimensions"],
    queryFn: listBudgetDimensions,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const invalidateGrants = () =>
    queryClient.invalidateQueries({ queryKey: scopeGrantsKey(userId) });

  const createMut = useMutation({
    mutationFn: (body) => createUserScopeGrant(userId, body),
    onSuccess: invalidateGrants,
  });
  const deleteMut = useMutation({
    mutationFn: (grantId) => deleteUserScopeGrant(userId, grantId),
    onSuccess: invalidateGrants,
  });

  const grants = grantsQ.data || [];
  const dimensions = useMemo(() => dimsQ.data || [], [dimsQ.data]);

  const dimById = useMemo(() => {
    const m = {};
    for (const d of dimensions) m[d.id] = d;
    return m;
  }, [dimensions]);
  const dimByCode = useMemo(() => {
    const m = {};
    for (const d of dimensions) m[d.code] = d;
    return m;
  }, [dimensions]);

  const [showAdd, setShowAdd] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [labelTouched, setLabelTouched] = useState(false);
  const [draftClauses, setDraftClauses] = useState([emptyDraftClause()]);
  const [removeCandidate, setRemoveCandidate] = useState(null);

  const openAdd = () => {
    setDraftLabel("");
    setLabelTouched(false);
    setDraftClauses([emptyDraftClause()]);
    setShowAdd(true);
  };
  const closeAdd = () => setShowAdd(false);

  const updateClause = (clauseId, patch) => {
    setDraftClauses((prev) =>
      prev.map((c) => (c.id === clauseId ? { ...c, ...patch } : c))
    );
  };

  const handleDimensionChange = (clauseId, opt) => {
    updateClause(clauseId, {
      dimensionId: opt?.value ?? null,
      nodeId: null,
      nodeLabel: null,
    });
  };

  const handleNodeChange = (clauseId, opt) => {
    updateClause(clauseId, {
      nodeId: opt?.value ?? null,
      nodeLabel: opt?.label ?? null,
    });
    // Default the grant label from the first clause's picked node, until the
    // admin edits the label themselves.
    if (!labelTouched && draftClauses[0]?.id === clauseId && opt?.label) {
      setDraftLabel(opt.label);
    }
  };

  const addClause = () =>
    setDraftClauses((prev) => [...prev, emptyDraftClause()]);

  const removeClause = (clauseId) => {
    setDraftClauses((prev) => {
      const next = prev.filter((c) => c.id !== clauseId);
      return next.length > 0 ? next : [emptyDraftClause()];
    });
  };

  const isDraftComplete =
    draftClauses.length > 0 &&
    draftClauses.every((c) => c.dimensionId != null && c.nodeId != null);

  const handleSave = () => {
    if (!isDraftComplete) {
      toast.error("Complete every condition (dimension + value) before saving.");
      return;
    }
    const clauses = draftClauses.map((c) => ({
      dimensionCode: dimById[c.dimensionId]?.code,
      operator: "in_subtree",
      nodeId: c.nodeId,
    }));
    const label = draftLabel.trim() || draftClauses[0]?.nodeLabel || "New scope";
    createMut.mutate(
      { label, clauses },
      {
        onSuccess: () => {
          toast.success("Scope grant added");
          setShowAdd(false);
        },
        onError: (err) => toast.error(friendlyGrantError(err, "add")),
      }
    );
  };

  const handleRemoveConfirm = () => {
    if (!removeCandidate) return;
    deleteMut.mutate(removeCandidate.id, {
      onSuccess: () => {
        toast.success("Scope grant removed");
        setRemoveCandidate(null);
      },
      onError: (err) => {
        setRemoveCandidate(null);
        toast.error(friendlyGrantError(err, "remove"));
      },
    });
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
      <div className="border-b border-border pb-3 mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text">Data scope</h3>
          <p className="text-xs text-text-2 mt-0.5">
            Restricts which organisation, product, or use-case data this user can see.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={openAdd}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-ctrl text-sm font-medium text-primary border border-solid border-primary hover:bg-primary-soft transition-colors cursor-pointer box-border"
          >
            <FontAwesomeIcon
              icon={icon({ name: "plus", style: "solid" })}
              className="text-xs"
            />
            Add scope
          </button>
        )}
      </div>

      {grantsQ.isLoading && (
        <div className="text-sm text-text-2">Loading data-scope grants…</div>
      )}

      {grantsQ.isError && (
        <div className="text-sm text-danger">Failed to load data-scope grants.</div>
      )}

      {!grantsQ.isLoading && !grantsQ.isError && grants.length === 0 && (
        <div className="text-sm text-text-2 italic">
          No data-scope grants. This user can see all data their role permits.
        </div>
      )}

      {!grantsQ.isLoading && grants.length > 0 && (
        <ul className="space-y-3 list-none m-0 p-0">
          {grants.map((grant) => (
            <li
              key={grant.id}
              className="rounded-lg border border-border bg-surface-2/50 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-text truncate">
                    {grant.label}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(grant.clauses || []).map((clause, idx) => (
                      <span
                        key={idx}
                        data-testid="scope-clause-chip"
                        className="inline-flex items-center gap-1 text-xs bg-card border border-border text-text px-2 py-1 rounded-ctrl"
                      >
                        {dimByCode[clause.dimensionCode]?.name || clause.dimensionCode}
                        <span className="text-text-2">&middot;</span>
                        {clause.nodeLabel || clause.nodeId}
                      </span>
                    ))}
                  </div>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setRemoveCandidate(grant)}
                    className="shrink-0 p-1.5 text-text-2 hover:text-danger transition-colors cursor-pointer border-0 bg-transparent box-border"
                    title="Remove scope grant"
                    aria-label={`Remove scope grant ${grant.label}`}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "trash", style: "solid" })}
                      className="text-xs"
                    />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Add scope */}
      <Modal
        show={showAdd}
        close={closeAdd}
        title="Add data scope"
        description="Restrict this user's data to a subtree of one or more dimensions. All conditions must match."
        confirmButton="Add scope"
        cancelButton="Cancel"
        confirmAction={handleSave}
        size="lg"
      >
        <div className="space-y-4" style={{ minWidth: "420px" }}>
          <Input
            title="Label"
            name="grantLabel"
            type="text"
            value={draftLabel}
            onChange={(e) => {
              setDraftLabel(e.target.value);
              setLabelTouched(true);
            }}
            placeholder="e.g. EMEA Marketing"
            noMarginBottom
          />

          <div className="space-y-3">
            {draftClauses.map((clause) => (
              <div key={clause.id} className="flex items-start gap-2">
                <div style={{ minWidth: "160px" }}>
                  <Dropdown
                    options={dimensions.map((d) => ({ value: d.id, label: d.name }))}
                    selectedValue={
                      clause.dimensionId != null
                        ? {
                            value: clause.dimensionId,
                            label: dimById[clause.dimensionId]?.name || "",
                          }
                        : null
                    }
                    setSelectedValue={(opt) => handleDimensionChange(clause.id, opt)}
                    fullWidth
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : undefined
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <ScopeNodeTreeSelect
                    dimensionId={clause.dimensionId}
                    value={clause.nodeId}
                    label={clause.nodeLabel}
                    onChange={(opt) => handleNodeChange(clause.id, opt)}
                    placeholder="Select a dimension first"
                  />
                </div>
                {draftClauses.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeClause(clause.id)}
                    className="mt-2 p-1.5 text-text-2 hover:text-danger transition-colors cursor-pointer border-0 bg-transparent box-border"
                    title="Remove condition"
                    aria-label="Remove condition"
                  >
                    <FontAwesomeIcon icon={icon({ name: "times", style: "solid" })} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addClause}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-ctrl text-sm font-medium text-text-2 border border-solid border-border hover:bg-surface-2 transition-colors cursor-pointer box-border"
          >
            <FontAwesomeIcon icon={icon({ name: "plus", style: "solid" })} className="text-xs" />
            Add condition
          </button>
        </div>
      </Modal>

      {/* Remove confirm */}
      <Modal
        show={!!removeCandidate}
        close={() => setRemoveCandidate(null)}
        title="Remove this scope grant?"
        description={
          removeCandidate
            ? `"${removeCandidate.label}" will no longer apply. The user may lose access to some data.`
            : ""
        }
        confirmButton="Remove"
        cancelButton="Cancel"
        alertIcon
        confirmAction={handleRemoveConfirm}
      />
    </div>
  );
};

UserScopeGrants.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default UserScopeGrants;
